import { FC } from 'react';
import styled from 'styled-components';
import MessageList from '@cps/MessageList';
import Navbar from '@cps/Navbar';
import Waiting from '@/components/Waiting';
import Network from '@/components/Network';

import Composer from '@cps/Composer';
import { useCreateChat } from '@/service/useChatConfig';
import { useNodes } from '@/service/useNodes';
import { EFaqKnows, useFaq, useMessageHistory } from '@/service/useChat';
import useMessageStore, { EMessageStatus, EMsgType, IMessage } from '@/store/messageStore';
import localforage from 'localforage';
import MediaPreview from '@cps/MediaPreview';
import MessageBus from '@/components/MessageBus';
import useUserStore, { sessionConfigCache } from '@/store/userStore';
import { clone, getLocalTime, removeRepeatKey } from '@/utils';
import { useDocumentVisibility, useTrackedEffect, useUpdateEffect } from 'ahooks';
import { recoverArray } from '@/utils';
import { useNetwork } from 'ahooks';
import useAppStore from '@/store/appStore';
import { useLang } from '@/locales/useLang';
import { useTranslation } from 'react-i18next';

const HomeWrap = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 900rem;
  margin: 0 auto;
  position: relative;
  box-shadow: 0 0 5px 0 rgba(0, 0, 0, 0.1);
  border-radius: 20rem;

  > footer {
    display: flex;
    align-items: center;
    border-top: solid 1px #ebebeb;
  }
`;

const Home: FC<{}> = () => {
  const { getNodes, nodes } = useNodes();
  const { sessionConfig, loading, createSession } = useCreateChat();
  const messageStore = useMessageStore();
  const userStore = useUserStore();
  const networkState = useNetwork();
  const appStore = useAppStore();
  const documentVisibility = useDocumentVisibility();
  const $t = useLang();

  const {
    seqInfo,
    isEndCurSession,
    history,
    moreHistory,
    getSessionList,
    getHistory,
    getMoreHistory,
    msgCompensation,
  } = useMessageHistory();

  useUpdateEffect(() => {
    if (documentVisibility === 'visible') {
      if (!appStore.isKickOff) {
        msgCompensation();
      }
    }
  }, [documentVisibility]);

  const initData = async () => {
    await createSession();
    const sessionList = await getSessionList();
    seqInfo.current.sessionList = sessionList;

    const { getSessionConfig } = sessionConfigCache();
    const { sessionConfig } = getSessionConfig();

    getHistory()
      .then(async (historyMsg) => {
        let localMsg =
          (await localforage.getItem<IMessage[]>(
            sessionConfig.uid + '__' + sessionConfig.sessionId + '-msg'
          )) || [];

        for (let i = 0, len = historyMsg.length; i < len; i++) {
          const historyMsgItem = historyMsg[i];
          const tempHistoryMsgItem = historyMsgItem.clientMsgID.split('-');
          const historyMsgItemId = tempHistoryMsgItem.slice(0, -1).join('-');
          historyMsg[i]['uniqueId'] = historyMsgItemId;

          for (const localMsgItem of localMsg) {
            if (historyMsgItemId === localMsgItem.uniqueId) {
              localMsg = localMsg.filter((item) => {
                // 上传成功的,直接删除缓存,,如果还需要的话,请表明注释
                if (item.status === EMessageStatus.success) {
                  return false;
                }
                // 上传非成功的用local
                return item.uniqueId !== historyMsgItemId;
              });
            }

            if (localMsgItem.status === EMessageStatus.pending) {
              localMsgItem.status = EMessageStatus.fail;
            }

            localMsgItem.isInitMsg = true;
          }

          historyMsg[i]['isInitMsg'] = true;
        }

        // 成功的：有用本地用本地，
        const messageList = recoverArray(localMsg, historyMsg, 'uniqueId');

        const maxMsgSeq = Math.max(...messageList.map((item) => item.msgSeq));

        messageStore.setLatestSeq(maxMsgSeq);
        messageStore.setMessageList(messageList);
        messageStore.setScrollMessage(messageList);

        localforage.setItem(
          sessionConfig?.uid + '__' + sessionConfig?.sessionId + '-msg',
          localMsg
        );
      })
      .catch(async () => {
        let localMsg =
          (await localforage.getItem<IMessage[]>(
            sessionConfig.uid + '__' + sessionConfig.sessionId + '-msg'
          )) || [];
        console.log('错误...', localMsg);

        messageStore.setMessageList(localMsg);
        messageStore.setScrollMessage(localMsg);
      })
      .finally(() => {
        messageStore.setIsInitHistory(true);
      });

    getNodes?.();
  };

  useEffect(() => {
    initData();
  }, []);

  return (
    <>
      <HomeWrap>
        <Navbar title={$t('chat_session_status_1')} />
        {!networkState.online && <Network />}
        <MediaPreview />

        {sessionConfig?.token && nodes?.ws?.[0] && (
          <MessageList
            getMoreMessage={async () => {
              if (seqInfo.current.curInx >= seqInfo.current.sessionList.length && isEndCurSession) {
                return;
              }
              let messageList: IMessage[] = [];
              if (isEndCurSession) {
                const historyMsg = await getHistory();

                let localMsg =
                  (await localforage.getItem<IMessage[]>(
                    sessionConfig.uid + '__' + sessionConfig.sessionId + '-msg'
                  )) || [];

                messageList = [...historyMsg, ...messageStore.messageList, ...localMsg];

                const cloneMsgList = clone(messageList, true);

                for (let i = 0, len = cloneMsgList.length; i < len; i++) {
                  const tempHistoryMsgItem = cloneMsgList[i].clientMsgID.split('-');
                  cloneMsgList[i].uniqueId = tempHistoryMsgItem.slice(0, -1).join('-');
                }
                messageList = removeRepeatKey(cloneMsgList, 'uniqueId');
              } else {
                const historyMsg = await getMoreHistory();

                let localMsg =
                  (await localforage.getItem<IMessage[]>(
                    sessionConfig.uid + '__' + sessionConfig.sessionId + '-msg'
                  )) || [];

                messageList = [...historyMsg, ...messageStore.messageList, ...localMsg];
                const cloneMsgList = clone(messageList, true);

                for (let i = 0, len = messageList.length; i < len; i++) {
                  const tempHistoryMsgItem = cloneMsgList[i].clientMsgID.split('-');
                  cloneMsgList[i].uniqueId = tempHistoryMsgItem.slice(0, -1).join('-');
                }

                messageList = removeRepeatKey(cloneMsgList, 'uniqueId');
              }

              const newList = messageList.map((item) => {
                // item.clientMsgID = item.clientMsgID || Math.random() + '-' + Date.now();

                if (item.status === EMessageStatus.pending) {
                  item.status = EMessageStatus.fail;
                }
                return item;
              });

              messageStore.setMessageList(newList);
              return newList;
            }}
          />
        )}

        <footer>
          <Composer />
        </footer>
      </HomeWrap>

      {!!nodes.ws?.[0] &&
        !!nodes.ws?.[0] &&
        !!sessionConfig?.sessionId &&
        !!messageStore.isInitHistory &&
        !appStore.isKickOff && <MessageBus />}
    </>
  );
};

export default Home;
